import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_ENDPOINTS } from "../config/constants";
import { getReadableApiError } from "../utils/readableApiResponse";
import { getTierName } from "../utils/subscription";

const AUTH_STORAGE_KEYS = {
    token: "token",
    refreshToken: "refreshToken",
    expiresIn: "expiresIn",
    refreshExpiresIn: "refreshExpiresIn",
    tokenType: "tokenType",
};

const LEGACY_AUTH_STORAGE_KEYS = [
    "user",
    "workspace",
    "credits",
    "csv_export_enabled",
];

const storedToken = localStorage.getItem(AUTH_STORAGE_KEYS.token);
const storedRefreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);
const storedExpiresIn = localStorage.getItem(AUTH_STORAGE_KEYS.expiresIn);
const storedRefreshExpiresIn = localStorage.getItem(
    AUTH_STORAGE_KEYS.refreshExpiresIn,
);
const storedTokenType = localStorage.getItem(AUTH_STORAGE_KEYS.tokenType);

const removeLegacyAuthStorage = () => {
    LEGACY_AUTH_STORAGE_KEYS.forEach((key) => {
        localStorage.removeItem(key);
    });
};

const persistAuthTokens = ({
    token,
    refreshToken,
    expiresIn,
    refreshExpiresIn,
    tokenType,
}) => {
    localStorage.setItem(AUTH_STORAGE_KEYS.token, token || "");
    localStorage.setItem(
        AUTH_STORAGE_KEYS.refreshToken,
        refreshToken || "",
    );
    localStorage.setItem(AUTH_STORAGE_KEYS.expiresIn, expiresIn || "");
    localStorage.setItem(
        AUTH_STORAGE_KEYS.refreshExpiresIn,
        refreshExpiresIn || "",
    );
    localStorage.setItem(AUTH_STORAGE_KEYS.tokenType, tokenType || "");
};

const getCsvExportEnabled = (payload) => payload?.csv_export_enabled === true;

const normalizeUserPlan = (user, credits) => {
    if (!user) return null;

    return {
        ...user,
        role: user.role || (user.is_admin ? "admin" : "user"),
        name: user.full_name || user.name || user.email,
        plan: getTierName(credits, user.plan),
    };
};

const getApiClient = async () => {
    const apiModule = await import("../utils/api");

    return apiModule.default;
};

const getAuthRequestErrorPayload = (error, fallbackMessage) => ({
    message: getReadableApiError(error, fallbackMessage),
    status: error.response?.status || null,
    data: error.response?.data || null,
});

export const fetchCurrentUser = createAsyncThunk(
    "auth/fetchCurrentUser",
    async (_, thunkAPI) => {
        try {
            const api = await getApiClient();
            const response = await api.get(API_ENDPOINTS.ACCESS_ME);

            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(error, "Failed to fetch current user."),
            );
        }
    },
    {
        condition: (_, { getState }) => {
            const { auth } = getState();

            return Boolean(auth?.token) && auth?.currentUserStatus !== "loading";
        },
    },
);

export const fetchCurrentUsage = createAsyncThunk(
    "auth/fetchCurrentUsage",
    async (_, thunkAPI) => {
        try {
            const api = await getApiClient();
            const response = await api.get(API_ENDPOINTS.ACCESS_USAGE);

            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(error, "Failed to fetch current usage."),
            );
        }
    },
);

export const forgotPassword = createAsyncThunk(
    "auth/forgotPassword",
    async (email, thunkAPI) => {
        try {
            const api = await getApiClient();
            const response = await api.post(API_ENDPOINTS.FORGOT_PASSWORD, {
                email,
            });

            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getAuthRequestErrorPayload(
                    error,
                    "Failed to request password reset.",
                ),
            );
        }
    },
);

export const resetPassword = createAsyncThunk(
    "auth/resetPassword",
    async ({ token, newPassword }, thunkAPI) => {
        try {
            const api = await getApiClient();
            const response = await api.post(API_ENDPOINTS.RESET_PASSWORD, {
                token,
                new_password: newPassword,
            });

            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getAuthRequestErrorPayload(
                    error,
                    "Failed to reset password.",
                ),
            );
        }
    },
);

const initialState = {
    token: storedToken || null,
    isAuthenticated: !!storedToken,
    user: null,
    refreshToken: storedRefreshToken || null,
    workspace: null,
    credits: null,
    csvExportEnabled: false,
    expiresIn: storedExpiresIn ? Number(storedExpiresIn) : null,
    refreshExpiresIn: storedRefreshExpiresIn
        ? Number(storedRefreshExpiresIn)
        : null,
    tokenType: storedTokenType || null,
    currentUserStatus: "idle",
    currentUserError: null,
    usageStatus: "idle",
    usageError: null,
    forgotPasswordStatus: "idle",
    forgotPasswordError: null,
    resetPasswordStatus: "idle",
    resetPasswordError: null,
};

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        loginSuccess: (state, action) => {
            const credits = action.payload.credits || null;
            const user = normalizeUserPlan(action.payload.user, credits);

            state.token = action.payload.token;
            state.isAuthenticated = true;
            state.user = user;
            state.refreshToken = action.payload.refreshToken;
            state.workspace = action.payload.workspace;
            state.credits = credits;
            state.csvExportEnabled = action.payload.csvExportEnabled === true;
            state.expiresIn = action.payload.expiresIn;
            state.refreshExpiresIn = action.payload.refreshExpiresIn;
            state.tokenType = action.payload.tokenType;
            persistAuthTokens(action.payload);
            removeLegacyAuthStorage();
        },
        refreshSessionSuccess: (state, action) => {
            const credits = action.payload.credits || state.credits || null;
            const user = normalizeUserPlan(
                action.payload.user || state.user,
                credits,
            );

            state.token = action.payload.token;
            state.isAuthenticated = true;
            state.user = user;
            state.refreshToken =
                action.payload.refreshToken || state.refreshToken;
            state.workspace = action.payload.workspace || state.workspace;
            state.credits = credits;
            if (action.payload.csvExportEnabled !== undefined) {
                state.csvExportEnabled =
                    action.payload.csvExportEnabled === true;
            }
            state.expiresIn = action.payload.expiresIn;
            state.refreshExpiresIn = action.payload.refreshExpiresIn;
            state.tokenType = action.payload.tokenType;
            state.currentUserStatus = "succeeded";
            state.currentUserError = null;
            persistAuthTokens({
                ...action.payload,
                refreshToken:
                    action.payload.refreshToken || state.refreshToken,
            });
            removeLegacyAuthStorage();
        },
        updateUser: (state, action) => {
            const user = normalizeUserPlan(action.payload, state.credits);

            state.user = user;
        },
        logout: (state) => {
            state.token = null;
            state.isAuthenticated = false;
            state.user = null;
            state.refreshToken = null;
            state.workspace = null;
            state.credits = null;
            state.csvExportEnabled = false;
            state.expiresIn = null;
            state.refreshExpiresIn = null;
            state.tokenType = null;
            state.currentUserStatus = "idle";
            state.currentUserError = null;
            state.usageStatus = "idle";
            state.usageError = null;
            state.forgotPasswordStatus = "idle";
            state.forgotPasswordError = null;
            state.resetPasswordStatus = "idle";
            state.resetPasswordError = null;
            localStorage.removeItem(AUTH_STORAGE_KEYS.token);
            localStorage.removeItem(AUTH_STORAGE_KEYS.refreshToken);
            localStorage.removeItem(AUTH_STORAGE_KEYS.expiresIn);
            localStorage.removeItem(AUTH_STORAGE_KEYS.refreshExpiresIn);
            localStorage.removeItem(AUTH_STORAGE_KEYS.tokenType);
            removeLegacyAuthStorage();
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCurrentUser.pending, (state) => {
                state.currentUserStatus = "loading";
                state.currentUserError = null;
            })
            .addCase(fetchCurrentUser.fulfilled, (state, action) => {
                const payload = action.payload || {};
                const credits = payload.credits || null;
                const user = normalizeUserPlan(payload.user || null, credits);

                state.user = user;
                state.workspace = payload.workspace || null;
                state.credits = credits;
                state.csvExportEnabled = getCsvExportEnabled(payload);
                state.currentUserStatus = "succeeded";
                state.currentUserError = null;
                state.usageStatus = "succeeded";
                state.usageError = null;
                removeLegacyAuthStorage();
            })
            .addCase(fetchCurrentUser.rejected, (state, action) => {
                state.currentUserStatus = "failed";
                state.currentUserError =
                    action.payload || "Failed to fetch current user.";
            })
            .addCase(fetchCurrentUsage.pending, (state) => {
                state.usageStatus = "loading";
                state.usageError = null;
            })
            .addCase(fetchCurrentUsage.fulfilled, (state, action) => {
                const credits = action.payload || state.credits || null;
                const user = normalizeUserPlan(state.user, credits);

                state.credits = credits;
                state.user = user;
                state.usageStatus = "succeeded";
                state.usageError = null;
            })
            .addCase(fetchCurrentUsage.rejected, (state, action) => {
                state.usageStatus = "failed";
                state.usageError =
                    action.payload || "Failed to fetch current usage.";
            })
            .addCase(forgotPassword.pending, (state) => {
                state.forgotPasswordStatus = "loading";
                state.forgotPasswordError = null;
            })
            .addCase(forgotPassword.fulfilled, (state) => {
                state.forgotPasswordStatus = "succeeded";
                state.forgotPasswordError = null;
            })
            .addCase(forgotPassword.rejected, (state, action) => {
                state.forgotPasswordStatus = "failed";
                state.forgotPasswordError =
                    action.payload || {
                        message: "Failed to request password reset.",
                        status: null,
                        data: null,
                    };
            })
            .addCase(resetPassword.pending, (state) => {
                state.resetPasswordStatus = "loading";
                state.resetPasswordError = null;
            })
            .addCase(resetPassword.fulfilled, (state) => {
                state.resetPasswordStatus = "succeeded";
                state.resetPasswordError = null;
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.resetPasswordStatus = "failed";
                state.resetPasswordError =
                    action.payload || {
                        message: "Failed to reset password.",
                        status: null,
                        data: null,
                    };
            });
    },
});

export const { loginSuccess, refreshSessionSuccess, updateUser, logout } =
    authSlice.actions;
export default authSlice.reducer;
